/**
 * member-resolver (US-AI.F2-0231 · @UC9400) — ĐỐI CHIẾU tên người trong câu chat với DANH SÁCH THÀNH
 * VIÊN THẬT của nhóm. Hàm THUẦN (không LLM, không DB) — caller nạp members từ DB rồi gọi.
 *
 * VÌ SAO TỒN TẠI (bug tiền THẬT, đã chứng minh):
 *  - @AI-WRG-01 / @SEC-WRONG-01 — trước đây `memberName` là CHUỖI THÔ đi thẳng vào bút toán: "Ghi nhận
 *    Minh đóng 500k" ⇒ quỹ +500k nhưng CÔNG NỢ của Minh KHÔNG giảm (bút toán không gắn memberId) ⇒
 *    LỆCH SỔ. Tên BỊA ("Ronaldo") vẫn ghi được vào sổ cái. Resolver này là cửa duy nhất: KHÔNG ra
 *    memberId THẬT ⇒ caller PHẢI từ chối, TUYỆT ĐỐI không post ledger.
 *  - @AI-WRG-03 — người Việt gọi nhau bằng mọi cách: "anh Minh", "minh", "Nguyễn Văn Minh", "Minh " —
 *    so khớp phải bỏ dấu + bỏ kính ngữ + so cả token, KHÔNG so chuỗi thô.
 *  - Hai "Minh" trong nhóm ⇒ AMBIGUOUS ⇒ hỏi lại (AI-INV: mơ hồ KHÔNG đoán).
 */

/** Thành viên THẬT trong nhóm (caller nạp từ group_members — id là groupMember.id). */
export interface GroupMemberRef {
  readonly memberId: string;
  /** Tên hiển thị (displayName per-group, fallback fullName) — có thể null (chưa đặt tên). */
  readonly displayName: string | null;
}

/** Kết quả đối chiếu tên → thành viên THẬT. */
export type MemberResolution =
  | { readonly status: "resolved"; readonly memberId: string; readonly displayName: string }
  | { readonly status: "not_found"; readonly nameRaw: string }
  | { readonly status: "ambiguous"; readonly nameRaw: string; readonly candidates: readonly GroupMemberRef[] };

/** Kính ngữ / đại từ xưng hô đứng TRƯỚC tên (strip trước khi so khớp) — @AI-WRG-03. */
const HONORIFIC_TOKENS: ReadonlySet<string> = new Set([
  "chi",
  "anh",
  "co",
  "chu",
  "bac",
  "em",
  "be",
  "ong",
  "ba",
  "thay",
  "cau",
  "mo",
  "di",
  "con",
  "chau",
  "ban",
]);

/**
 * US-AIV2-0111 C06 — chữ chỉ NGƯỜI NHÀ của một bé ("mẹ/bố/cha/má"), chỉ bị bỏ khi đứng trước một từ chỉ trẻ (xem `normalizeMemberName`).
 * 🔴 AI-REDUNDANT-ASK (@UC0199 @UC0200, đo 10–11/10 `thang`/`hanh`): + "nhà" — *"anh nhận được tiền của NHÀ bé Gia Bảo rồi"* là cách phụ huynh/thủ quỹ
 * gọi suất của bé như "mẹ bé Gia Bảo". MỘT nguồn (chữ CÒN DẤU) cho resolver tên (bỏ dấu, ở đây) VÀ khuôn câu "tiền của X đã về" (`enum-slot.ts`,
 * so theo từ còn dấu) — không chép tập lần hai. Hẹp như C06: CHỈ khi ngay sau là `CHILD_WORDS` ("tiền nhà" — tiền thuê nhà — không bị đụng).
 */
export const PARENT_OF_WORDS: readonly string[] = Object.freeze(["mẹ", "bố", "cha", "má", "nhà"]);
/** Từ chỉ TRẺ đứng sau `PARENT_OF_WORDS` ("mẹ BÉ X", "nhà CHÁU X"). */
export const CHILD_WORDS: readonly string[] = Object.freeze(["bé", "con", "cháu"]);
const plainToken = (w: string): string => w.normalize("NFD").replace(/\p{M}+/gu, "").replace(/đ/gu, "d");
const PARENT_OF_TOKENS: ReadonlySet<string> = new Set(PARENT_OF_WORDS.map(plainToken));
const CHILD_TOKENS: ReadonlySet<string> = new Set(CHILD_WORDS.map(plainToken));

/**
 * Chuẩn hoá tên tiếng Việt để so khớp: bỏ dấu (NFD strip combining), đ→d, lowercase, gộp khoảng trắng,
 * bỏ kính ngữ đứng đầu. "Anh Minh" / "minh" / " MINH " ⇒ "minh".
 */
export function normalizeMemberName(raw: string): string {
  const base = (raw ?? "")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .replace(/đ/gu, "d")
    .replace(/Đ/gu, "D")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
  if (base === "") return "";
  const parts = base.split(" ");
  /*
   * 🔴 US-AIV2-0111 C06 (@UC0200) — "mẹ bé X" / "bố cháu X" / "cha con X" gọi SUẤT của bé X (đo 28/09: RECORD-PAID gạch
   * "mẹ bé Tuấn Kiệt" ⇒ not_found ⇒ hỏi "Ai đã đóng?" không kèm lựa chọn). HẸP: chỉ bỏ "mẹ/bố/cha/má" khi NGAY SAU là
   * "bé/con/cháu" — "Mẹ Bin" (tên người lớn trong nhóm) giữ nguyên. Kính ngữ còn lại bỏ như cũ ở vòng dưới.
   */
  const tokens = parts.length >= 2 && PARENT_OF_TOKENS.has(parts[0]!) && CHILD_TOKENS.has(parts[1]!) ? parts.slice(1) : parts;
  // Strip kính ngữ ở ĐẦU (nhiều lớp: "anh chị Minh"); KHÔNG strip nếu strip xong rỗng (tên = "em"?
  // ⇒ không có tên thật, trả rỗng để caller not_found — AI-INV không đoán).
  let i = 0;
  while (i < tokens.length && HONORIFIC_TOKENS.has(tokens[i]!)) i++;
  const rest = tokens.slice(i);
  return rest.join(" ");
}

/**
 * Tên đệm "lót" phổ biến — MỘT token này đứng riêng không phải cách gọi ai ("Văn"/"Thị" trong "Nguyễn Văn Hùng"/"Trần Thị Lan").
 * Chỉ dùng cho luật "một token gọi được ai" dưới đây; khi nó là token CUỐI (người tên "Văn") vẫn là tên gọi.
 */
const MIDDLE_FILLER_TOKENS: ReadonlySet<string> = new Set(["van", "thi"]);

/**
 * Với họ-tên `hayTokens`, MỘT token người gõ có thể "gọi" được người này khi nó là token nào?
 *  - tên ≤2 token ("Minh Anh", "Ngọc Lan", "Minh"): MỌI token — tên gọi kép, "Minh" gọi được "Minh Anh" (ví dụ @UC0161);
 *  - tên ≥3 token ("Nguyễn Văn Hùng", "Nguyễn Thị Minh Anh"): BỎ token đầu (họ — "Nguyễn" một mình không gọi ai) và BỎ
 *    đệm lót "văn"/"thị" trừ khi là token cuối; còn lại là tên gọi (đơn hoặc kép): "Minh"/"Anh" gọi được "Nguyễn Thị Minh Anh".
 */
function callableTokens(hayTokens: readonly string[]): readonly string[] {
  if (hayTokens.length <= 2) return hayTokens;
  const last = hayTokens.length - 1;
  return hayTokens.filter((t, i) => i > 0 && (i === last || !MIDDLE_FILLER_TOKENS.has(t)));
}

/**
 * Tên có "khớp" thành viên không? Khớp khi:
 *  - trùng hoàn toàn sau chuẩn hoá ("minh" ≡ "Minh"), HOẶC
 *  - người gõ MỘT token và token đó GỌI ĐƯỢC người này (`callableTokens`: "minh" ⊂ "minh anh" · "hung" ⊂ "nguyen van hung" ·
 *    "minh" ⊂ "nguyen thi minh anh"; nhưng "nguyen"/"van"/"thi" KHÔNG gọi được "Nguyễn Văn Hùng"), HOẶC
 *  - người gõ NHIỀU token và mọi token đều là token NGUYÊN VẸN của họ-tên ("van minh" ⊂ "nguyen van minh"), HOẶC
 *  - tên trong câu chứa trọn họ-tên (user gõ đầy đủ, DB lưu tên gọi) — "nguyen van minh" ⊃ "minh".
 * KHÔNG khớp lỏng theo substring bất kỳ (tránh "min" ⇒ "Minh": ghi nhầm người = lệch sổ).
 *
 * 🔴 US-AIV2-0022 (@UC0161) — vế 2 ĐỔI từ "token CUỐI" thành "token GỌI ĐƯỢC". Lý do đo được: nhóm có "Minh Anh" và
 * "Minh Khôi", người gõ "Minh" ⇒ bản cũ `not_found` (token cuối là "anh"/"khoi") ⇒ trợ lý hỏi trống "Ai đã đóng?" thay vì
 * hỏi kèm `[Minh Anh] [Minh Khôi]` — đúng ví dụ của UC.
 *
 * KHAI ĐỦ CHIỀU (review #815 M3): tập khớp mới ⊇ tập khớp cũ (token cuối luôn ∈ `callableTokens`) ⇒ `resolved` cũ → `resolved`
 * hoặc `ambiguous`; `ambiguous` cũ → `ambiguous`; và có ĐƯỜNG MỚI `not_found` → `resolved`/`ambiguous` cho token gọi được ở vị trí
 * không-cuối ("Minh" với duy nhất "Minh Anh" ⇒ resolved — ý của UC). Đường mới đó CỐ Ý KHÔNG mở cho họ/đệm lót: "Nguyễn"/"Văn"/
 * "Thị"/"Trần" với một "Nguyễn Văn Hùng" vẫn `not_found` (khoá bằng test) — một mình họ/đệm không phải cách gọi ai, resolve
 * là đoán. Substring vẫn cấm: "min" không phải token của ai.
 */
function matches(needle: string, member: GroupMemberRef): boolean {
  const hay = normalizeMemberName(member.displayName ?? "");
  if (hay === "" || needle === "") return false;
  if (hay === needle) return true;
  const hayTokens = hay.split(" ");
  const needleTokens = needle.split(" ");
  // một token: phải là token GỌI ĐƯỢC (không phải họ/đệm lót đứng riêng của tên ≥3 token).
  if (needleTokens.length === 1) return callableTokens(hayTokens).includes(needle);
  // nhiều token: mọi token người gõ đều là token nguyên vẹn của họ-tên trong sổ ("van minh" ⊂ "nguyen van minh").
  if (needleTokens.every((t) => hayTokens.includes(t))) return true;
  // sổ lưu tên gọi (1 token), người gõ họ-tên đầy đủ: "nguyen van minh" ⊃ "minh".
  if (hayTokens.length === 1 && needleTokens.at(-1) === hay) return true;
  return false;
}

/**
 * 🔴 US-AIV2-0111 C07g (NEED C03 · @UC0203) — hậu tố "người đó ỨNG/TRẢ TRƯỚC" mà LLM đôi khi gạch LẪN vào ô tên (đo 28/09: ô người
 * ứng = "Quân ứng trước", 1/3 lượt ⇒ `not_found` ⇒ hỏi lại tên vừa được nói). Dạng ĐÃ chuẩn hoá (bỏ dấu, chữ thường), dài trước.
 * Chỉ dùng làm LƯỢT THỬ LẠI khi cả cụm không khớp ai (`resolveMember`) — cụm khớp nguyên vẹn (người tên "Ứng", "Trà") không bị đụng.
 */
export const PAYER_SUFFIXES: readonly string[] = Object.freeze([
  "ung tien truoc",
  "tra tien truoc",
  "ung truoc",
  "tra truoc",
  "ung tien",
  "tra tien",
  "ung",
  "tra",
]);

/** Bỏ MỘT hậu tố `PAYER_SUFFIXES` ở cuối cụm đã chuẩn hoá (theo TỪ nguyên vẹn); không có ⇒ trả nguyên. Chỉ còn hậu tố ⇒ `""`. */
export function stripPayerSuffix(normalized: string): string {
  for (const suffix of PAYER_SUFFIXES) {
    if (normalized === suffix) return "";
    if (normalized.endsWith(` ${suffix}`)) return normalized.slice(0, -suffix.length - 1).trim();
  }
  return normalized;
}

/**
 * Đối chiếu tên thô (do classifier rút từ câu chat) với DANH SÁCH THÀNH VIÊN THẬT.
 *
 * @AI-WRG-01 @SEC-WRONG-01 — KHÔNG bao giờ trả memberId "đoán": không khớp ⇒ `not_found` (caller từ
 * chối, 0 bút toán); khớp NHIỀU ⇒ `ambiguous` (caller hỏi lại, liệt kê ứng viên).
 * @AI-WRG-03 — khớp bất kể kính ngữ / dấu / hoa-thường / họ-tên đầy đủ.
 */
export function resolveMember(
  nameRaw: string | null | undefined,
  members: readonly GroupMemberRef[],
): MemberResolution {
  const raw = (nameRaw ?? "").trim();
  const needle = normalizeMemberName(raw);
  if (needle === "") return { status: "not_found", nameRaw: raw };

  const full = members.filter((m) => matches(needle, m));
  // US-AIV2-0111 C07g: cả cụm không khớp ai ⇒ thử lại MỘT lần sau khi bỏ hậu tố "ứng trước/trả trước…" (xem `stripPayerSuffix`).
  const stripped = full.length === 0 ? stripPayerSuffix(needle) : needle;
  const hits = stripped !== needle && stripped !== "" ? members.filter((m) => matches(stripped, m)) : full;
  if (hits.length === 0) return { status: "not_found", nameRaw: raw };
  if (hits.length > 1) return { status: "ambiguous", nameRaw: raw, candidates: Object.freeze([...hits]) };

  const hit = hits[0]!;
  return {
    status: "resolved",
    memberId: hit.memberId,
    displayName: hit.displayName ?? raw,
  };
}
